import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, Clock, ArrowLeft, ArrowRight, ChevronRight, Phone } from "lucide-react";
import SEO from "@/components/SEO";
import TripCard from "@/components/TripCard";
import ShareButtons from "@/components/ShareButtons";
import StickyShareBar from "@/components/StickyShareBar";
import { getPostBySlug, getRecentPosts } from "@/data/blog";
import { getTripBySlug } from "@/data/trips";

const SITE_URL = "https://clubamistad-madrid.com";

function fmtDate(d) {
  return new Date(d).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const { meta, content } = post;
  const url = `${SITE_URL}/blog/${meta.slug}`;

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: meta.title,
    description: meta.description,
    image: `${SITE_URL}${meta.cover}`,
    datePublished: meta.date,
    dateModified: meta.date,
    author: { "@type": "Organization", name: meta.author, url: SITE_URL },
    publisher: { "@id": `${SITE_URL}/#organization` },
    mainEntityOfPage: url,
    keywords: meta.keywords?.join(", "),
    inLanguage: "es-ES",
  };

  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Blog", item: `${SITE_URL}/blog` },
      { "@type": "ListItem", position: 3, name: meta.title, item: url },
    ],
  };

  const faqLd = meta.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: meta.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  const jsonLdBundle = [articleLd, breadcrumbLd, faqLd].filter(Boolean);
  const recent = getRecentPosts(3, meta.slug);
  const relatedTrips = (meta.related_trips || [])
    .map((slug) => getTripBySlug(slug))
    .filter(Boolean);

  return (
    <>
      <SEO
        title={`${meta.title} · Blog Club Amistad`}
        description={meta.description}
        path={`/blog/${meta.slug}`}
        image={`${SITE_URL}${meta.cover}`}
        jsonLd={jsonLdBundle}
      />
      <div className="pt-[76px] md:pt-[96px] bg-brand-bg min-h-screen">
        <StickyShareBar url={url} title={meta.title} />
        {/* HERO */}
        <section className="section-y bg-brand-surface/40 border-b border-brand-line">
          <div className="container-prose max-w-4xl">
            <nav
              className="flex items-center gap-2 text-xs uppercase tracking-wider text-brand-muted mb-6"
              aria-label="Miga de pan"
            >
              <Link to="/" className="hover:text-brand-primary">Inicio</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to="/blog" className="hover:text-brand-primary">Blog</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-brand-primary">{meta.category}</span>
            </nav>

            <div className="flex items-center gap-3 text-xs text-brand-muted mb-4">
              <span className="uppercase tracking-wider font-medium text-brand-accent">{meta.category}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {fmtDate(meta.date)}</span>
              <span>·</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {meta.reading_minutes} min de lectura</span>
            </div>

            <h1 className="font-outfit text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-brand-primary">
              {meta.title}
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-brand-ink/75 leading-relaxed">
              {meta.description}
            </p>
          </div>
        </section>

        {/* COVER */}
        <div className="relative bg-brand-surface">
          <div className="container-prose max-w-4xl -mt-1">
            <div className="aspect-[16/9] overflow-hidden rounded-b-3xl">
              <img
                src={meta.cover}
                alt={meta.cover_alt}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* CONTENT */}
        <section className="section-y">
          <div className="container-prose max-w-3xl">
            <article
              data-testid="blog-post-content"
              className="prose-blog"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
                {content}
              </ReactMarkdown>
            </article>

            <ShareButtons url={url} title={meta.title} />

            {/* RELATED TRIPS — enlace interno contextual con anchors ricos en keywords */}
            {relatedTrips.length > 0 && (
              <div className="mt-14" data-testid="related-trips">
                <p className="overline">Viajes relacionados</p>
                <h2 className="h2-display mt-3 mb-8">Viajes que encajan con lo que has leído.</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {relatedTrips.map((t, i) => (
                    <TripCard key={t.slug} trip={t} index={i} />
                  ))}
                </div>
              </div>
            )}

            {/* INTERNAL CTA */}
            <div className="mt-14 rounded-3xl border border-brand-line bg-brand-surface/60 p-8 md:p-10 text-center">
              <p className="overline">¿Listos para reservar?</p>
              <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-brand-primary mt-3">
                Hablamos con vosotros y montamos vuestro viaje sin compromiso.
              </h2>
              <p className="mt-4 text-sm md:text-base text-brand-muted max-w-xl mx-auto">
                Una persona real de Club Amistad os llama para entender qué necesita vuestra familia
                y os pasa un presupuesto detallado. Sin coste, sin letra pequeña.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/#viajes"
                  data-testid="post-cta-trips"
                  className="btn-accent"
                >
                  Ver todos los viajes
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href="tel:+34914020331"
                  data-testid="post-cta-call"
                  className="btn-ghost"
                >
                  <Phone className="h-4 w-4" /> Llamar 914 020 331
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* RECENT POSTS */}
        {recent.length > 0 && (
          <section className="section-y bg-brand-surface/40 border-t border-brand-line">
            <div className="container-prose max-w-5xl">
              <p className="overline">Sigue leyendo</p>
              <h2 className="h2-display mt-3 mb-10">Más guías del blog</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {recent.map((p) => (
                  <Link
                    key={p.meta.slug}
                    to={`/blog/${p.meta.slug}`}
                    className="card-soft overflow-hidden hover:-translate-y-1 transition-transform"
                  >
                    <div className="aspect-[16/10] overflow-hidden">
                      <img src={p.meta.cover} alt={p.meta.cover_alt} loading="lazy" className="h-full w-full object-cover" />
                    </div>
                    <div className="p-5">
                      <p className="text-xs uppercase tracking-wider text-brand-accent">{p.meta.category}</p>
                      <h3 className="font-outfit text-lg text-brand-primary font-semibold mt-2">{p.meta.title}</h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <div className="border-t border-brand-line">
          <div className="container-prose max-w-3xl py-6">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-primary">
              <ArrowLeft className="h-4 w-4" /> Volver al blog
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

// Componentes markdown con estilos alineados al design system del sitio.
const mdComponents = {
  h1: ({ node, ...props }) => (
    <h1 className="font-outfit text-3xl md:text-4xl font-semibold text-brand-primary mt-10 mb-6 leading-tight" {...props} />
  ),
  h2: ({ node, ...props }) => (
    <h2 className="font-outfit text-2xl md:text-3xl font-semibold text-brand-primary mt-12 mb-4 leading-tight" {...props} />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="font-outfit text-xl md:text-2xl font-semibold text-brand-primary mt-8 mb-3" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="font-manrope text-brand-ink/85 text-base md:text-lg leading-relaxed mb-5" {...props} />
  ),
  a: ({ node, href, ...props }) => {
    const isInternal = href?.startsWith("/") && !href.startsWith("//");
    if (isInternal) {
      return (
        <Link
          to={href}
          className="text-brand-accent underline underline-offset-2 hover:text-brand-primary"
          {...props}
        />
      );
    }
    return (
      <a
        href={href}
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
        className="text-brand-accent underline underline-offset-2 hover:text-brand-primary"
        {...props}
      />
    );
  },
  ul: ({ node, ...props }) => (
    <ul className="list-disc pl-6 space-y-2 mb-6 text-brand-ink/85 font-manrope" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="list-decimal pl-6 space-y-2 mb-6 text-brand-ink/85 font-manrope" {...props} />
  ),
  li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />,
  strong: ({ node, ...props }) => <strong className="font-semibold text-brand-primary" {...props} />,
  em: ({ node, ...props }) => <em className="italic" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote className="border-l-4 border-brand-accent pl-5 my-6 italic font-cormorant text-brand-primary text-xl" {...props} />
  ),
  table: ({ node, ...props }) => (
    <div className="my-8 overflow-x-auto rounded-2xl border border-brand-line">
      <table className="w-full text-sm md:text-base" {...props} />
    </div>
  ),
  thead: ({ node, ...props }) => <thead className="bg-brand-surface" {...props} />,
  th: ({ node, ...props }) => (
    <th className="text-left px-4 py-3 font-outfit font-semibold text-brand-primary border-b border-brand-line" {...props} />
  ),
  td: ({ node, ...props }) => (
    <td className="px-4 py-3 border-t border-brand-line align-top text-brand-ink/85" {...props} />
  ),
  hr: () => <hr className="my-10 border-t border-brand-line" />,
  code: ({ node, inline, ...props }) =>
    inline ? (
      <code className="rounded bg-brand-surface px-1.5 py-0.5 font-mono text-sm text-brand-primary" {...props} />
    ) : (
      <code className="block p-4 bg-brand-primary text-white rounded-xl font-mono text-sm overflow-x-auto" {...props} />
    ),
};
